import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import styled from 'styled-components';
import {
  cancelCurrentSubscription,
  fetchPricingPlanAccountOverview,
} from '../../duck/actions/accountId';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '../../../../model/State';
import { selectPricingPlanOverview } from '../../duck/selectors';
import { StyledLink } from '../licensesExpandedRow/StyledComponents';
import Textfield from '@atlaskit/textfield';
import { Dimensions } from '../../../../components/visuals';
import { colors } from '@atlaskit/theme';
import { messages } from './messages';
import config from 'appconfig';

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  height: ${Dimensions.units(18.75)}px;
`;

const TextfieldWrapper = styled.div`
  padding: ${Dimensions.units(1.25)}px 0px;
  width: ${Dimensions.units(31)}px;
`;

const LoadingState = () => (
  <>
    <ModalBody>
      <SpinnerWrapper>
        <Spinner size="large" />
      </SpinnerWrapper>
    </ModalBody>
  </>
);

const CancelSubscriptionModalBody = ({ accountDetails, handleChangeText }) => {
  const dispatch = useDispatch();
  const pricingPlanOverview = useSelector((state) =>
    selectPricingPlanOverview(state as State, accountDetails.id),
  );

  useEffect(() => {
    if (accountDetails && accountDetails.id) {
      dispatch(fetchPricingPlanAccountOverview(accountDetails.id));
    }
  }, [accountDetails]);

  const activeProducts = pricingPlanOverview?.products.filter(
    (i) => i.status === 'ACTIVE' && i.suspended === false,
  );

  const externallyManagedProducts = activeProducts?.filter(
    (i) => i.productKey === 'atlassian-whiteboard' || i.productKey === 'statuspage',
  );

  const internallyManagedProducts = activeProducts?.filter(
    (i) => i.productKey !== 'atlassian-whiteboard' && i.productKey !== 'statuspage',
  );

  return !pricingPlanOverview ? (
    <LoadingState />
  ) : (
    <>
      <ModalBody>
        <SectionMessage
          actions={[
            <SectionMessageAction
              key="contact-action"
              href={`${config.companyContact}/purchasing-licensing`}
            >
              <FormattedMessage {...messages.contactMessage} />
            </SectionMessageAction>,
          ]}
        >
          <p>
            <FormattedMessage {...messages.financialDifficulties} />
          </p>
        </SectionMessage>
        {internallyManagedProducts.length > 0 ? (
          <p>
            <FormattedMessage
              {...messages.cancelProductsModal}
              values={{
                cloudSite: <a href={accountDetails.accountUrl}>{accountDetails.accountUrl}</a>,
              }}
            />
          </p>
        ) : (
          <p>
            <FormattedMessage
              {...messages.licensesCancelSiteModalMessage}
              values={{
                cloudSite: <a href={accountDetails.accountUrl}>{accountDetails.accountUrl}</a>,
              }}
            />
          </p>
        )}
        <ul>
          {activeProducts.map((product) => (
            <li key={product.product.id}>{product.product.description}</li>
          ))}
        </ul>
        {externallyManagedProducts.length > 0 && (
          <p>
            <FormattedMessage {...messages.externallyManagedProducts} />
            <ul>
              {externallyManagedProducts.map((product) => (
                <li key={product.product.id}>{product.product.description}</li>
              ))}
            </ul>
          </p>
        )}
        <p>
          <FormattedMessage {...messages.siteAccessInfo} />
          <b>
            <FormattedDate
              value={new Date(accountDetails.latestPeriod.endDate)}
              year="numeric"
              month="short"
              day="2-digit"
            />
          </b>
        </p>
        <p>
          <StyledLink href="https://confluence.atlassian.com/cloud/cancellations-873871347.html">
            <FormattedMessage {...messages.learnAboutCancelling} />
          </StyledLink>
        </p>
        <hr />
        <p>
          <b>
            <FormattedMessage
              {...messages.licensesCancelSiteModalConfirmation}
              values={{
                siteUrl: accountDetails.accountUrl,
              }}
            />
          </b>
        </p>
        <p style={{ color: `${colors.N300}` }}>
          <FormattedMessage {...messages.typeToContinue} />
        </p>
        <TextfieldWrapper>
          <Textfield
            data-cy="cancel-confirmation-input"
            onChange={handleChangeText}
            style={{ height: `${Dimensions.units(3.75)}px` }}
          />
        </TextfieldWrapper>
      </ModalBody>
    </>
  );
};

interface Props {
  accountDetails: any;
  closeModal: () => void;
  openSurveyModal: () => void;
}

const CancelSubscriptionModal = (props: Props) => {
  const dispatch = useDispatch();

  const [isDisabledButton, setIsDisabledButton] = useState<boolean>(true);

  const handleChangeText = (e) => {
    if (e.target && e.target.value.toUpperCase() === 'CANCEL') setIsDisabledButton(false);
    else setIsDisabledButton(true);
  };

  const handleCancelSubscription = () => {
    dispatch(
      cancelCurrentSubscription(props.accountDetails.id, props.openSurveyModal, props.closeModal),
    );
  };

  return (
    <>
      <ModalTransition>
        <Modal>
          <ModalHeader>
            <ModalTitle appearance="danger">
              <FormattedMessage {...messages.cancelCloudSiteCTA} />
            </ModalTitle>
            <IconButton
              appearance="subtle"
              icon={CrossIcon}
              label="Close Modal"
              onClick={props.closeModal}
            />
          </ModalHeader>
          <CancelSubscriptionModalBody
            accountDetails={props.accountDetails}
            handleChangeText={handleChangeText}
          />
          <ModalFooter>
            <Button appearance="subtle" onClick={props.closeModal}>
              <FormattedMessage {...messages.doNotCancelSite} />
            </Button>
            <Button
              data-cy="confirm-cancel-button"
              isDisabled={isDisabledButton}
              appearance="primary"
              onClick={handleCancelSubscription}
            >
              <FormattedMessage {...messages.permanentlyCancelSite} />
            </Button>
          </ModalFooter>
        </Modal>
      </ModalTransition>
    </>
  );
};

export default CancelSubscriptionModal;
